/**
 * Firestore — RFP Pack v2.1.
 *
 * Database   : default
 * Collection : /workflows/rfp-packs/projects/{packId}
 *
 * Lives on the generic /workflows/{workflowSlug}/projects/{projectId} path
 * (see firestore.rules) so owner-gating works out of the box:
 *   create/update/delete require request.auth != null and
 *   the doc's `created_by` field == the caller's uid.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
} from "firebase/firestore";
import { getFirebaseFirestore } from "@/platform/firebase/client";
import { getUserProfile, setFreePackUsed as markFreePackUsed } from "@/platform/firebase/firestore/users";
import type { RFPack, RFPContext } from "@/modes/agentic-workflows/rfp-pack/types";
import { FREE_PACK_LIMIT } from "@/modes/agentic-workflows/rfp-pack/types";
import { assemblePack, buildContext } from "@/modes/agentic-workflows/rfp-pack/orchestrator";
import { getPlaybook, categoryLabel } from "@/modes/agentic-workflows/rfp-pack/playbooks";
import { emptyContext } from "@/modes/agentic-workflows/rfp-pack/types";

const WF_SLUG = "rfp-packs";
const COL = "projects";
function db() { return getFirebaseFirestore(); }
function projectRef(id: string) { return doc(db(), "workflows", WF_SLUG, COL, id); }
function projectCol() { return collection(db(), "workflows", WF_SLUG, COL); }

/**
 * Authoritative free-tier gate — reads the persistent profile flag AND the
 * live pack count in a single call, so a deletion in the same session never
 * restores the consumed allowance.
 *
 * Returns `true` when the user is on the free tier and has no remaining
 * RFP Pack allowance (flag set OR at least one pack ever created).
 */
export async function freePackAllowanceUsed(userId: string): Promise<boolean> {
  const profile = await getUserProfile(userId).catch(() => null);
  if (profile?.freePackUsed) return true;
  return (await countUserPacks(userId)) >= FREE_PACK_LIMIT;
}

/** Set the persistent free-pack-used marker on the user's profile. */
export async function setFreePackUsed(userId: string): Promise<void> {
  await markFreePackUsed(userId);
}

/** Recursively drop undefined values so setDoc/updateDoc never reject. */
function cleanForWrite<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => cleanForWrite(v)) as unknown as T;
  }
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue;
      out[k] = cleanForWrite(v);
    }
    return out as T;
  }
  return value;
}

export interface CreatePackInput {
  name: string;
  client_name: string;
  category_id: RFPack["category_id"];
  context: RFPContext;
  workspace: "consultant" | "company";
  sourceDraftText?: string;
  sourceDraftName?: string;
}

export async function createPack(
  userId: string,
  userEmail: string,
  input: CreatePackInput,
): Promise<RFPack> {
  const id = `rfpp-${Date.now()}`;
  // ensure pack_id populated before assembly so citations reference it
  const ctx = buildContext(input.category_id, {
    ...input.context,
    meta: { ...input.context.meta, pack_id: id },
  });
  const pack = assemblePack({
    id,
    name: input.name,
    client_name: input.client_name,
    category_id: input.category_id,
    created_by: userId,
    created_by_email: userEmail,
    workspace: input.workspace,
    context: ctx,
    sourceDraftText: input.sourceDraftText,
    sourceDraftName: input.sourceDraftName,
  });
  await setDoc(projectRef(id), cleanForWrite(pack));
  return pack;
}

export async function createPackFromScratch(
  userId: string,
  input: CreatePackInput,
): Promise<RFPack> {
  return createPack(userId, "", input);
}

export async function getPack(id: string): Promise<RFPack | null> {
  const snap = await getDoc(projectRef(id));
  return snap.exists() ? (snap.data() as RFPack) : null;
}

/**
 * Count a user's packs — used by the free-tier generation limit gate.
 * Owner-restricted query (created_by == uid), identical to getUserPacks.
 */
export async function countUserPacks(userId: string): Promise<number> {
  const q = query(projectCol(), where("created_by", "==", userId));
  const snap = await getDocs(q);
  return snap.size;
}

export async function getUserPacks(userId: string): Promise<RFPack[]> {
  const q = query(projectCol(), where("created_by", "==", userId), limit(100));
  const snap = await getDocs(q);
  const packs = snap.docs.map((d) => d.data() as RFPack);
  return packs.sort((a, b) => b.updated_at?.localeCompare(a.updated_at ?? "") || 0);
}

export async function updatePack(id: string, data: Partial<RFPack>): Promise<void> {
  const clean = { ...data };
  delete (clean as Record<string, unknown>).id;
  await updateDoc(projectRef(id), {
    ...cleanForWrite(clean),
    updated_at: new Date().toISOString(),
  });
}

/**
 * Re-generate the pack content from the current context (context drawer).
 * Recomputes sections, artifacts, quality, grade. Leaves id/timestamps intact.
 */
export async function regeneratePack(id: string, pack: RFPack): Promise<RFPack> {
  const regenerated = assemblePack({
    id: pack.id,
    name: pack.name,
    client_name: pack.client_name,
    category_id: pack.category_id,
    created_by: pack.created_by,
    created_by_email: pack.created_by_email,
    workspace: pack.workspace,
    context: pack.context,
    sourceDraftText: pack.source_draft_text,
    sourceDraftName: pack.source_draft_name,
    removedSections: pack.removed_sections,
    manualSections: pack.manual_sections,
    waivedGaps: pack.waived_gaps ?? [],
  });
  const now = new Date().toISOString();
  const merged: RFPack = {
    ...regenerated,
    id: pack.id,
    created_at: pack.created_at,
    updated_at: now,
    stale: false,
    waived_gaps: pack.waived_gaps ?? [],
    // preserve RAG ground-truth across the full-setDoc replace
    rag_source_status: pack.rag_source_status,
    rag_provenance: pack.rag_provenance,
  };
  await setDoc(projectRef(id), cleanForWrite(merged));
  return merged;
}

export async function duplicatePack(id: string, userId: string, userEmail: string): Promise<RFPack> {
  const orig = await getPack(id);
  if (!orig) throw new Error("Pack not found");
  const newId = `rfpp-${Date.now()}-dup`;
  const ctx = {
    ...orig.context,
    meta: { ...orig.context.meta, pack_id: newId },
  };
  const pack = assemblePack({
    id: newId,
    name: `${orig.name} (copy)`,
    client_name: orig.client_name,
    category_id: orig.category_id,
    created_by: userId,
    created_by_email: userEmail,
    workspace: orig.workspace,
    context: ctx,
    sourceDraftText: orig.source_draft_text,
    sourceDraftName: orig.source_draft_name,
  });
  await setDoc(projectRef(newId), cleanForWrite(pack));
  return pack;
}

export async function deletePack(id: string): Promise<void> {
  await deleteDoc(projectRef(id));
}

/**
 * Delete a pack AND its isolated RAG corpus + stored draft (data isolation).
 * Firestore delete always runs; the RAG wipe is best-effort and non-blocking
 * — if the backend/Vertex is down the row is still removed.
 */
export async function deletePackWithRag(id: string): Promise<void> {
  await deleteDoc(projectRef(id));
  const base =
    (typeof window !== "undefined" &&
      (window as unknown as Record<string, string | undefined>).NEXT_PUBLIC_RFP_BACKEND) ||
    "http://localhost:8088";
  const form = new FormData();
  form.append("pack_id", id);
  try {
    await fetch(`${base}/api/rfp/packs/rag/wipe`, { method: "POST", body: form });
  } catch {
    /* non-blocking */
  }
}

// convenience re-exports so screens don't import orchestrator directly
export { buildContext, getPlaybook, categoryLabel, emptyContext };
export type { RFPContext };